"use client";

import { MapPin } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function DirectDetails() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const hours = [{
    day: "MON",
    time: "09 — 17",
    terminusRight: true
  }, {
    day: "TUE — FRI",
    time: "09 — 18",
    terminusRight: false
  }, {
    day: "SAT",
    time: "10 — 14",
    terminusRight: true
  }, {
    day: "SUN",
    time: "CLOSED",
    terminusRight: false
  }];
  return <section data-section-id="2142" id="direct-details" className="bg-background text-foreground relative overflow-hidden border-t" style={{
    borderTopColor: "var(--border)"
  }}>
      <div className="container mx-auto px-5 md:px-12 lg:px-18 py-24 md:py-32 max-w-[1440px]">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-6 relative">
          {}
          <div aria-hidden="true" className="pointer-events-none absolute -top-12 left-0 lg:left-[-2rem] font-serif font-extralight select-none leading-none text-foreground/5" style={{
          fontSize: "clamp(12rem, 22vw, 22rem)",
          fontWeight: 200
        }}>
            06
          </div>

          {}
          <div ref={leftRef} className={`lg:col-span-6 relative z-10 transition-all duration-[640ms] ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
          transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)"
        }}>
            {}
            <div className="flex items-center gap-4 mb-10">
              <span className="block h-px w-6" style={{
              backgroundColor: "var(--chart-1)"
            }} />
              <span className="font-mono uppercase text-foreground" style={{
              fontSize: "clamp(0.6875rem, 0.78vw, 0.8125rem)",
              fontWeight: 500,
              letterSpacing: "0.18em"
            }}>
                § N° 06 — CONNECT
              </span>
            </div>

            {}
            <h2 className="font-default text-foreground mb-2" style={{
            fontSize: "clamp(2.25rem, 4.2vw, 4.25rem)",
            fontWeight: 600,
            lineHeight: 1.02,
            letterSpacing: "-0.02em"
          }}>
              Architecting your{" "}
              <em className="font-mono not-italic bg-accent text-accent-foreground px-2" style={{
              fontStyle: "normal"
            }}>
                autonomy
              </em>
              .
            </h2>

            {}
            <div className="flex items-center gap-2 mb-12">
              <span className="block h-px w-32" style={{
              backgroundColor: "var(--chart-1)"
            }} />
              <span className="block h-1.5 w-1.5 rounded-lg" style={{
              backgroundColor: "var(--foreground)"
            }} />
            </div>

            {}
            <div className="mb-12">
              <p className="font-mono uppercase mb-4 text-foreground/60" style={{
              fontSize: "0.6875rem",
              letterSpacing: "0.18em",
              fontWeight: 500
            }}>
                Headquarters
              </p>
              <address className="not-italic font-default text-foreground space-y-1" style={{
              fontSize: "clamp(1.5rem, 2.2vw, 2.125rem)",
              fontWeight: 400,
              lineHeight: 1.15,
              letterSpacing: "-0.012em"
            }}>
                <div className="!font-mono">AutolexaAi</div>
                <div className="text-foreground/85 !font-mono">44 Kerferd St</div>
                <div className="text-foreground/85 !font-mono">Rochester VIC 3561</div>
              </address>
            </div>

            {}
            <div className="mb-12">
              <p className="font-mono uppercase mb-6 text-foreground/60" style={{
              fontSize: "0.6875rem",
              letterSpacing: "0.18em",
              fontWeight: 500
            }}>
                Operational Hours
              </p>
              <div className="flex flex-col">
                {hours.map((row, i) => <div key={i} data-index={i} className="group relative flex items-center justify-between py-5 transition-colors duration-300 hover:bg-foreground/[0.02]">
                    {}
                    <span className="absolute top-0 left-0 right-0 h-px" style={{
                  backgroundColor: "var(--border)"
                }} />
                    {}
                    <span className={`absolute top-[-3px] block h-1.5 w-1.5 rounded-lg transition-opacity duration-300 group-hover:opacity-100 opacity-70 ${row.terminusRight ? "right-0" : "left-0"}`} style={{
                  backgroundColor: "var(--foreground)"
                }} />
                    <span className="font-mono uppercase text-foreground" style={{
                  fontSize: "0.8125rem",
                  letterSpacing: "0.18em",
                  fontWeight: 500
                }}>
                      {row.day}
                    </span>
                    <span className="font-mono text-foreground/85" style={{
                  fontSize: "0.8125rem",
                  letterSpacing: "0.08em",
                  fontWeight: 400
                }}>
                      {row.time}
                    </span>
                  </div>)}
                {}
                <div className="relative">
                  <span className="absolute top-0 left-0 right-0 h-px" style={{
                  backgroundColor: "var(--border)"
                }} />
                </div>
              </div>
            </div>

            {}
            <Link to="/ConnectPage" aria-label="Get directions to AutolexaAi">
              <Button className="bg-foreground/70 text-background hover:bg-foreground/60 px-8 py-6 h-auto font-default uppercase transition-all duration-[220ms] hover:-translate-y-0.5" style={{
              fontSize: "0.8125rem",
              fontWeight: 500,
              letterSpacing: "0.04em",
              boxShadow: "0 0 0 0 transparent"
            }} onMouseEnter={e => {
              e.currentTarget.style.boxShadow = "0 16px 32px -16px var(--ring)";
            }} onMouseLeave={e => {
              e.currentTarget.style.boxShadow = "0 0 0 0 transparent";
            }}>
                <MapPin className="size-4" />
                Get Directions
              </Button>
            </Link>
          </div>

          {}
          <div ref={rightRef} className={`lg:col-span-6 relative z-10 transition-all duration-[640ms] ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
          transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)",
          transitionDelay: "80ms"
        }}>
            <div className="relative">
              {}
              <div className="relative overflow-hidden" style={{
              aspectRatio: "4 / 5"
            }}>
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/autolexaai_hq_modern_office.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/autolexaai_hq_modern_office.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/autolexaai_hq_modern_office.webp-wvc-sizes" alt="AutolexaAi modern office space with digital automation infrastructure" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />

                {}
                {}
                <span aria-hidden="true" className="absolute top-3 left-3 w-2 h-2 border-t border-l" style={{
                borderColor: "var(--muted-foreground)"
              }} />
                {}
                <span aria-hidden="true" className="absolute top-3 right-3 w-2 h-2 border-t border-r" style={{
                borderColor: "var(--muted-foreground)"
              }} />
                {}
                <span aria-hidden="true" className="absolute bottom-3 left-3 w-2 h-2 border-b border-l" style={{
                borderColor: "var(--muted-foreground)"
              }} />
                {}
                <span aria-hidden="true" className="absolute bottom-3 right-3 w-2 h-2 border-b border-r" style={{
                borderColor: "var(--foreground)"
              }} />
              </div>

              {}
              <div className="mt-5 flex items-center justify-between gap-4">
                <span className="font-mono uppercase text-foreground/70" style={{
                fontSize: "0.6875rem",
                letterSpacing: "0.08em",
                fontWeight: 400
              }}>
                  ROCHESTER-VIC / 36.4994° S / 144.7033° E
                </span>
                <span className="block h-px flex-1 max-w-[80px]" style={{
                backgroundColor: "var(--border)"
              }} />
                <span className="block h-1.5 w-1.5 rounded-lg shrink-0" style={{
                backgroundColor: "var(--foreground)"
              }} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}